import type { App } from "vue";
import type { Router } from "vue-router";
import vueGtag from "vue-gtag-next";
import * as Sentry from "@sentry/vue";
import VueHotJar from "vue-hotjar-next";

let started = false;
let clarityStarted = false;

export type TelemetryEnv = {
  isTelemetryEnabled: boolean;
  GA_ID?: string;
  HOTJAR_ID?: number;
  SENTRY_DSN?: string;
  SENTRY_ENV?: string;
  CLARITY_ID?: string;
};

function initClarity(projectId: string): void {
  if (clarityStarted) return;
  if (typeof window === "undefined") return;

  const w = window as any;
  if (w.clarity) {
    clarityStarted = true;
    return;
  }

  if (document.querySelector("script[src*=\"clarity.ms/tag/\"]")) {
    clarityStarted = true;
    return;
  }

  (function (c: any, l: Document, a: string, r: string, i: string, t?: HTMLScriptElement, y?: Element) {
    c[a] = c[a] || function (...args: unknown[]): void {
      (c[a].q = c[a].q || []).push(args);
    };
    t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    y?.parentNode?.insertBefore(t, y);
  })(window, document, "clarity", "script", projectId);

  clarityStarted = true;
}

export function initTelemetry({ app, router, env }: {
  app: App; router: Router; env: TelemetryEnv;
}): void {
  if (started || !env.isTelemetryEnabled) return;
  started = true;

  if (env.CLARITY_ID) {
    initClarity(env.CLARITY_ID);
  }

  if (env.SENTRY_DSN && env.SENTRY_ENV) {
    Sentry.init({
      app,
      dsn: env.SENTRY_DSN,
      environment: env.SENTRY_ENV,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true })
      ],
      tracesSampleRate: 0.1,
      sendDefaultPii: false,
      beforeSend(event) {
        try {
          if (event.request?.url) {
            const u = new URL(event.request.url);
            u.search = "";
            event.request.url = u.toString();
          }
        } catch {}
        if (event.user) {
          delete event.user.email;
          delete event.user.username;
          delete event.user.ip_address;
        }
        return event;
      }
    });
  }

  if (env.HOTJAR_ID && Number.isFinite(env.HOTJAR_ID) && env.HOTJAR_ID > 0) {
    app.use(VueHotJar, { id: env.HOTJAR_ID });
  }

  if (env.GA_ID) {
    app.use(vueGtag, { property: { id: env.GA_ID } });
    // Best-effort anonymize IP once gtag is available
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("config", env.GA_ID, { anonymize_ip: true });
    }
  }
}
